import {
  createContext,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useLazyLoadQuery, useMutation } from "react-relay";
import moment from "moment";

import {
  InspectionListQuery,
  InspectionListUpdatableFragmentQuery,
  MutationInFlightQuery,
} from "./Queries";
import { AuthContext, fetchUserID } from "../Auth";
import {
  ColumnFallback,
  Container,
  InspectionListScrollContainer,
  TaskList,
} from "./Styles";
import { ListColumnStates, StateColors } from "./StateHelpers";
import { FieldCardButtons } from "../../components/InspectionField";
import { usePopulateFilters } from "../../hooks/useFilters";
import AppContainer from "../../components/AppContainer";
import InspectionListColumn from "./components/InspectionListColumn";
import PlanModalContainer, {
  SelectedInspection,
} from "../../components/PlanModal";
import type { QueriesInspectionListQuery as InspectionListQueryType } from "./__generated__/QueriesInspectionListQuery.graphql";

export const ColumnDataContext = createContext<{
  disabledButtons?: FieldCardButtons[];
  setDisabledButtons: Function;
  planMultiple?: boolean;
  setPlanMultiple: Function;
  setPlannedDate: Function;
  setShowPlanModal: Function;
  selectedIds?: string[];
  setSelectedIds: Function;
  isMutationInFlight?: boolean;
  isDragging?: boolean;
}>({
  setDisabledButtons: () => {},
  setPlanMultiple: () => {},
  setPlannedDate: () => {},
  setShowPlanModal: () => {},
  setSelectedIds: () => {},
});

export const InspectionListContainer = memo(function Detail(props: {}) {
  return (
    <AppContainer page="/lijst">
      <Suspense fallback={<Fallback />}>
        <FieldList />
      </Suspense>
    </AppContainer>
  );
});

function FieldList() {
  const columnContainer = useRef<HTMLDivElement>(null);
  const [disabledButtons, setDisabledButtons] = useState<FieldCardButtons[]>(
    []
  );
  const [planMultiple, setPlanMultiple] = useState<boolean>(false);
  const [plannedDate, setPlannedDate] = useState<string>();
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isMutationInFlight, setIsMutationInFlight] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState(false);
  const authContext = useContext(AuthContext);

  const data = useLazyLoadQuery<InspectionListQueryType>(InspectionListQuery, {
    userId: authContext?.user ? fetchUserID(authContext?.user) : null,
  });
  const [commitMutation, isMutationInFlightQuery] = useMutation(
    MutationInFlightQuery
  );
  useEffect(() => {
    setIsMutationInFlight(isMutationInFlightQuery);
  }, [isMutationInFlightQuery]);

  const selectedInspections: SelectedInspection[] = useMemo(() => {
    return (
      data.allInspectionfields?.edges
        .filter((edge) =>
          selectedIds.some((selectedId) => selectedId === edge?.node?.id)
        )
        .filter((edge) => edge !== undefined)
        .map((edge) => {
          return {
            id: edge?.node?.id,
            externalId: edge?.node?.externalId,
            year: edge?.node?.year,
          } as SelectedInspection;
        }) || []
    );
  }, [data.allInspectionfields?.edges, selectedIds]);

  const updateField = (node: any, state: string) => {
    // TODO: This has always thrown a console error, but we should try to fix it.
    // "Warning: A store update was detected within another store update.
    // Please make sure new store updates aren't being executed within an updater function for a different update."
    if (!node) return;
    commitMutation({
      variables: {
        id: node?.id,
        timestamp: Date.now() / 1000.0,
        state: state,
      },
      optimisticUpdater: (store) => {
        const fragment = InspectionListUpdatableFragmentQuery;
        const { updatableData } = store.readUpdatableFragment_EXPERIMENTAL(
          fragment,
          node
        );
        const newDate = moment().format();
        if (state === "ASSIGNED") {
          updatableData.plannedDate = null;
        } else if (state === "PLANNED") {
          updatableData.plannedDate = newDate;
        } else if (state === "BUSY") {
          updatableData.lastModified = newDate;
        }
        if (updatableData.state === "RETRY" && state === "ASSIGNED") {
          updatableData.retry = true;
        }
        updatableData.state = state;
      },
    });
  };
  const handleBeforeCapture = () => {
    setIsDragging(true);
    if (columnContainer?.current?.style) {
      // To hide the horizontal scrollbar before drag & dropping tickets.
      columnContainer.current.style.height = `${columnContainer.current.clientHeight}px`;
      columnContainer.current.style.overflowX = "hidden";
      columnContainer.current.style.overflowY = "hidden";
    }
  };
  const handleDragEnd = (result: any) => {
    setIsDragging(false);
    if (columnContainer?.current?.style) {
      // To show the horizontal scrollbar after drag & dropping tickets.
      columnContainer.current.style.overflowX = "auto";
      columnContainer.current.style.overflowY = "hidden";
      columnContainer.current.style.height = "100%";
    }
    if (!result.destination || !result.source) {
      return;
    }
    updateField(
      data.allInspectionfields?.edges?.find(
        (edge) => edge?.node?.id === result.draggableId
      )?.node,
      result?.destination?.droppableId
    );
  };
  const handlePlanModalDismiss = useCallback(() => {
    setSelectedIds([]);
    setPlannedDate(undefined);
    setShowPlanModal(false);
  }, []);
  usePopulateFilters(data?.allInspectionfields);
  return (
    <DragDropContext
      onBeforeCapture={handleBeforeCapture}
      onDragEnd={handleDragEnd}
    >
      <ColumnDataContext.Provider
        value={{
          disabledButtons,
          setDisabledButtons,
          planMultiple,
          setPlanMultiple,
          setPlannedDate,
          setShowPlanModal,
          selectedIds,
          setSelectedIds,
          isMutationInFlight,
          isDragging,
        }}
      >
        <InspectionListScrollContainer ref={columnContainer}>
          <Container>
            {ListColumnStates.map((state) => (
              <InspectionListColumn
                key={state}
                state={state}
                updateField={updateField}
              />
            ))}
          </Container>
        </InspectionListScrollContainer>
        {showPlanModal && (
          <PlanModalContainer
            selectedInspections={selectedInspections}
            show={showPlanModal}
            date={plannedDate ? plannedDate : undefined}
            onDismiss={handlePlanModalDismiss}
            onSuccess={() => {
              setSelectedIds([]);
            }}
          />
        )}
      </ColumnDataContext.Provider>
    </DragDropContext>
  );
}

const Fallback = memo(function Comp() {
  return (
    <Container>
      {ListColumnStates.map((state) => (
        <ColumnFallback key={state} color={StateColors[state]}>
          <TaskList itemType={state}></TaskList>
        </ColumnFallback>
      ))}
    </Container>
  );
});

export default InspectionListContainer;
